import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import UserRegistrationModal from './UserRegistrationModal';
import TutorialModal from './TutorialModal';
import SoftvativeHeader from './SoftvativeHeader';
import SoftvativeFooter from './SoftvativeFooter';
import useDeviceDetection from '../hooks/useDeviceDetection';
import * as FiIcons from 'react-icons/fi';
import { getPlayerStats } from '../utils/leaderboardManager';
import { getCurrentUser, isUserLoggedIn, initializeUserSystem } from '../utils/userManager';

const { 
  FiUsers, FiCpu, FiPlay, FiClock, FiTrophy, FiUser, 
  FiBarChart3, FiBook, FiHelpCircle, FiShield, FiMenu, FiX 
} = FiIcons;

const ResponsiveGameModeSelection = () => {
  const navigate = useNavigate();
  const { isMobile, isTablet, screenSize } = useDeviceDetection();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [selectedMode, setSelectedMode] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState('');
  const [showAdminAccess, setShowAdminAccess] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    initializeUserSystem();
    const user = getCurrentUser();
    if (user && isUserLoggedIn()) {
      setCurrentPlayer(user);
    }

    const timer = setTimeout(() => {
      setShowAdminAccess(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const modes = [
    {
      id: 'two-player',
      title: 'Two Players',
      description: 'Play against a friend locally',
      icon: FiUsers,
      color: 'from-blue-500 to-purple-600'
    },
    {
      id: 'computer',
      title: 'vs Computer',
      description: 'Challenge the AI opponent',
      icon: FiCpu,
      color: 'from-green-500 to-blue-600'
    }
  ];

  const handleModeSelect = (mode) => {
    if (!currentPlayer || !isUserLoggedIn()) {
      setSelectedMode(mode);
      setShowRegistrationModal(true);
    } else {
      navigate(`/game/${mode}`);
    }
  };

  const handleRegistrationSuccess = (username) => {
    setCurrentPlayer(username);
    setShowRegistrationModal(false);
    if (selectedMode) {
      navigate(`/game/${selectedMode}`);
    }
  };

  const handleStartTutorial = () => {
    setShowTutorialModal(false);
    navigate('/tutorial');
  };

  const getCurrentPlayerStats = () => {
    if (!currentPlayer) return null;
    return getPlayerStats(currentPlayer);
  };

  const playerStats = getCurrentPlayerStats();

  const navigationItems = [
    { 
      icon: FiTrophy, 
      label: 'Leaderboard', 
      action: () => navigate('/leaderboard'),
      color: 'yellow'
    },
    {
      icon: FiBarChart3,
      label: 'My Stats',
      action: () => navigate('/stats'),
      show: !!currentPlayer,
      color: 'purple'
    },
    {
      icon: FiUser,
      label: currentPlayer ? 'Account' : 'Register',
      action: () => setShowRegistrationModal(true),
      color: 'gray'
    },
    {
      icon: FiHelpCircle,
      label: 'Help',
      action: () => setShowTutorialModal(true),
      color: 'indigo'
    }
  ];

  if (showAdminAccess) {
    navigationItems.push({
      icon: FiShield,
      label: 'Admin',
      action: () => navigate('/admin/login'),
      color: 'red'
    });
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Mobile Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-black/30 backdrop-blur-sm border-b border-white/10 p-4 flex items-center justify-between sticky top-0 z-40"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">♔</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Chess Master</h1>
              <p className="text-xs text-gray-300">Mobile Edition</p>
            </div>
          </div>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowMobileMenu(true)}
            className="p-2 bg-white/10 rounded-lg text-white"
          >
            <SafeIcon icon={FiMenu} className="text-xl" />
          </motion.button>
        </motion.header>

        {/* Main Content */}
        <div className="flex-1 p-4 pb-20">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-bold text-white mb-2">
              Welcome to Chess
            </h2>
            <div className="flex items-center justify-center gap-2 text-yellow-400">
              <SafeIcon icon={FiClock} />
              <span className="text-sm">2-minute timer per move</span>
            </div>
          </motion.div>

          {/* Player Stats Card */}
          {currentPlayer && playerStats && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 mb-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <SafeIcon icon={FiUser} className="text-blue-400 text-xl" />
                <div>
                  <h3 className="text-white font-bold">{currentPlayer}</h3>
                  <p className="text-gray-300 text-sm">Welcome back!</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-yellow-400">{playerStats.rating}</div>
                  <div className="text-xs text-gray-400">Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-400">{playerStats.wins}</div>
                  <div className="text-xs text-gray-400">Wins</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-400">{playerStats.winRate.toFixed(1)}%</div>
                  <div className="text-xs text-gray-400">Win Rate</div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Tutorial Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileTap={{ scale: 0.98 }}
            className="bg-gradient-to-br from-orange-500 to-red-600 p-4 rounded-xl shadow-lg border border-white/10 mb-6 cursor-pointer"
            onClick={() => setShowTutorialModal(true)}
          >
            <div className="flex items-center gap-4">
              <SafeIcon icon={FiBook} className="text-3xl text-white" />
              <div className="flex-1">
                <h3 className="text-white font-bold">Learn Chess</h3>
                <p className="text-white/80 text-sm">Interactive tutorial for beginners</p>
              </div>
            </div>
          </motion.div>

          {/* Game Mode Cards */}
          <div className="space-y-4">
            {modes.map((mode, index) => (
              <motion.div
                key={mode.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                whileTap={{ scale: 0.98 }}
                className={`bg-gradient-to-br ${mode.color} p-6 rounded-xl shadow-lg cursor-pointer`}
                onClick={() => handleModeSelect(mode.id)}
              >
                <div className="flex items-center gap-4">
                  <SafeIcon icon={mode.icon} className="text-4xl text-white" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{mode.title}</h3>
                    <p className="text-white/80 text-sm mb-3">{mode.description}</p>
                    <div className="flex items-center gap-2 text-white/90">
                      <SafeIcon icon={FiPlay} />
                      <span className="font-medium">Start Game</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {showMobileMenu && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                onClick={() => setShowMobileMenu(false)}
              />
              
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                className="fixed right-0 top-0 h-full w-80 max-w-[90vw] bg-slate-900 z-50 flex flex-col"
              >
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <h2 className="text-xl font-bold text-white">Menu</h2>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowMobileMenu(false)}
                    className="p-2 text-gray-400"
                  >
                    <SafeIcon icon={FiX} className="text-xl" />
                  </motion.button>
                </div>
                
                <div className="flex-1 p-4 space-y-2">
                  {navigationItems.map((item, index) => {
                    if (item.show === false) return null;
                    
                    return (
                      <motion.button
                        key={index}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          item.action();
                          setShowMobileMenu(false);
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors bg-${item.color}-600 hover:bg-${item.color}-700 text-white`}
                      >
                        <SafeIcon icon={item.icon} className="text-xl" />
                        <span className="font-medium">{item.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Modals */}
        <UserRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => setShowRegistrationModal(false)}
          onSuccess={handleRegistrationSuccess}
          currentName={currentPlayer}
          mode={currentPlayer ? 'update' : 'register'}
        />
        
        <TutorialModal
          isOpen={showTutorialModal}
          onClose={() => setShowTutorialModal(false)}
          onStartTutorial={handleStartTutorial}
        />
      </div>
    );
  }

  // Desktop/Tablet Layout (existing layout)
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl w-full"
        >
          <SoftvativeHeader />
          
          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-6xl font-bold text-white mb-4"
            >
              ♔ Chess Master ♛
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-300 flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiClock} className="text-yellow-400" />
              2-minute timer per move
            </motion.p>
          </div>

          {/* Player Info */}
          {currentPlayer && playerStats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 mb-8 max-w-md mx-auto"
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <SafeIcon icon={FiUser} className="text-blue-400" />
                  <h3 className="text-lg font-bold text-white">Welcome back, {currentPlayer}!</h3>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-xl font-bold text-yellow-400">{playerStats.rating}</div>
                    <div className="text-gray-400">Rating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-400">{playerStats.wins}</div>
                    <div className="text-gray-400">Wins</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-blue-400">{playerStats.winRate.toFixed(1)}%</div>
                    <div className="text-gray-400">Win Rate</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Tutorial Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-8"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gradient-to-br from-orange-500 to-red-600 p-6 rounded-2xl shadow-2xl border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer"
              onClick={() => setShowTutorialModal(true)}
            >
              <div className="text-center">
                <div className="mb-4">
                  <SafeIcon icon={FiBook} className="text-5xl text-white mx-auto group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Learn Chess</h3>
                <p className="text-white/80 mb-4">
                  New to chess? Start with our interactive tutorial to learn the rules and basic strategies
                </p>
                <div className="flex items-center justify-center gap-2 text-white/90 group-hover:text-white transition-colors">
                  <SafeIcon icon={FiPlay} />
                  <span className="font-semibold">Start Tutorial</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto mb-8">
            {modes.map((mode, index) => (
              <motion.div
                key={mode.id}
                initial={{ opacity: 0, x: index === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group cursor-pointer"
                onClick={() => handleModeSelect(mode.id)}
              >
                <div className={`bg-gradient-to-br ${mode.color} p-8 rounded-2xl shadow-2xl border border-white/10 hover:border-white/30 transition-all duration-300`}>
                  <div className="text-center">
                    <div className="mb-6">
                      <SafeIcon icon={mode.icon} className="text-6xl text-white mx-auto group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">{mode.title}</h3>
                    <p className="text-white/80 mb-6">{mode.description}</p>
                    <div className="flex items-center justify-center gap-2 text-white/90 group-hover:text-white transition-colors">
                      <SafeIcon icon={FiPlay} />
                      <span className="font-semibold">Start Game</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="flex justify-center gap-4 flex-wrap"
          >
            {navigationItems.map((item, index) => {
              if (item.show === false) return null;
              
              return (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={item.action}
                  className={`flex items-center gap-2 px-6 py-3 bg-${item.color}-600 hover:bg-${item.color}-700 text-white rounded-lg font-semibold transition-colors`}
                >
                  <SafeIcon icon={item.icon} />
                  {item.label}
                </motion.button>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="text-center mt-8 text-gray-400"
          >
            <p>Choose your game mode to begin playing and climb the leaderboard!</p>
          </motion.div>
        </motion.div>

        <UserRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => setShowRegistrationModal(false)}
          onSuccess={handleRegistrationSuccess}
          currentName={currentPlayer}
          mode={currentPlayer ? 'update' : 'register'}
        />

        <TutorialModal
          isOpen={showTutorialModal}
          onClose={() => setShowTutorialModal(false)}
          onStartTutorial={handleStartTutorial}
        />
      </div>

      <SoftvativeFooter />
    </div>
  );
};

export default ResponsiveGameModeSelection;